import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaUsers, FaUserTie, FaCheck, FaTimes, FaClipboardList, FaCalendar, FaFlag, FaClock, FaEdit } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DASHBOARD_STATS_CACHE_KEY = 'dashboardStatsCache';
const DASHBOARD_DATA_CACHE_KEY = 'dashboardDataCache';
const CACHE_DURATION = 30000; // 30 seconds cache duration

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [servicemenList, setServicemenList] = useState([]);
  const [referralsList, setReferralsList] = useState([]);
  const [bookingsList, setBookingsList] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [username, setUsername] = useState("");
  const [activeTab, setActiveTab] = useState("customers");
  const isFetchingBookingsRef = useRef(false);
  const isFetchingRef = useRef(false);
  const navigate = useNavigate();
  const { hasPermission, PERMISSIONS } = useAuth();

  // Modal state for drilldown lists
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalItems, setModalItems] = useState([]);

  // Get cached dashboard stats
  const getCachedStats = () => {
    try {
      const cached = localStorage.getItem(DASHBOARD_STATS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading stats cache:', e);
    }
    return null;
  };

  // Clear old cache entries to free up space
  const clearOldCache = () => {
    try {
      const cacheKeys = ['customersDataCache', 'servicemenDataCache', 'dashboardDataCache', 'dashboardStatsCache', 'bookingsDataCache'];
      cacheKeys.forEach(key => {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const { timestamp } = JSON.parse(cached);
            const now = Date.now();
            // Clear cache older than 1 minute
            if (now - timestamp > 60000) {
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          // Ignore errors for individual cache entries
        }
      });
    } catch (e) {
      // Ignore errors
    }
  };

  // Save dashboard stats to cache with quota handling
  const saveStatsToCache = (statsData) => {
    try {
      clearOldCache();
      const cacheData = {
        data: statsData,
        timestamp: Date.now()
      };
      localStorage.setItem(DASHBOARD_STATS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        try {
          localStorage.removeItem(DASHBOARD_STATS_CACHE_KEY);
        } catch (clearError) {
          // Ignore
        }
      }
      // Silently fail
    }
  };

  // Get cached dashboard data (customers, servicemen, referrals)
  const getCachedDashboardData = () => {
    try {
      const cached = localStorage.getItem(DASHBOARD_DATA_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading dashboard data cache:', e);
    }
    return null;
  };

  // Save dashboard data to cache with quota handling
  const saveDashboardDataToCache = (data) => {
    try {
      clearOldCache();

      // Reduce data size by only storing essential fields
      const reducedData = {
        customers: (data.customers || []).map(item => ({
          id: item.id,
          name: item.name,
          email: item.email,
          phone: item.phone,
          createdAt: item.createdAt,
          registeredAt: item.registeredAt,
          lastSeen: item.lastSeen,
          active: item.active,
          city: item.city
        })),
        servicemen: (data.servicemen || []).map(item => ({
          id: item.id,
          name: item.name,
          email: item.email,
          phone: item.phone,
          availabilityStart: item.availabilityStart,
          approved: item.approved,
          havePaid: item.havePaid,
          flagged: item.flagged,
          city: item.city,
          area: item.area
        })),
        referrals: (data.referrals || []).map(item => ({
          id: item.id,
          referrerId: item.referrerId,
          referredId: item.referredId,
          createdAt: item.createdAt,
          status: item.status
        }))
      };

      const cacheData = {
        data: reducedData,
        timestamp: Date.now()
      };
      localStorage.setItem(DASHBOARD_DATA_CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        try {
          localStorage.removeItem(DASHBOARD_DATA_CACHE_KEY);
          localStorage.removeItem('customersDataCache');
          localStorage.removeItem('servicemenDataCache');
          localStorage.removeItem('bookingsDataCache');
        } catch (clearError) {
          // Ignore
        }
      }
      // Silently fail - app will work without cache
    }
  };

  const fetchDashboardStats = async (showLoader = false) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current && showLoader) {
      return;
    }

    try {
      if (showLoader) {
        isFetchingRef.current = true;
      }

      const response = await fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/stats");
      if (!response.ok) {
        throw new Error("Failed to fetch dashboard statistics");
      }
      const data = await response.json();
      setStats(data);
      saveStatsToCache(data);
      return true;
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
      // Don't set error if we have cached data
      const cached = getCachedStats();
      if (!cached) {
        setError(err.message);
      }
      return false;
    } finally {
      if (showLoader) {
        isFetchingRef.current = false;
      }
    }
  };

  const fetchCustomersAndServicemen = async (showLoader = false) => {
    try {
      const fetchPromises = [
        fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/users"),
        fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen")
      ];

      // Only fetch referrals if user has permission
      if (hasPermission(PERMISSIONS.REFERRALS.VIEW)) {
        fetchPromises.push(fetch("https://us-central1-workexpress-1ffff.cloudfunctions.net/app/referrals"));
      }

      const responses = await Promise.all(fetchPromises);
      const [usersRes, svcRes, referralsRes] = responses;

      const dataPromises = [usersRes.json(), svcRes.json()];
      if (hasPermission(PERMISSIONS.REFERRALS.VIEW) && referralsRes) {
        dataPromises.push(referralsRes.json());
      }

      const dataResults = await Promise.all(dataPromises);
      const [usersData, svcData, referralsData] = dataResults;

      const customersArray = Array.isArray(usersData) ? usersData : [usersData];
      const servicemenArray = Array.isArray(svcData) ? svcData : [svcData];

      setCustomerList(customersArray);
      setServicemenList(servicemenArray);

      if (hasPermission(PERMISSIONS.REFERRALS.VIEW) && referralsData) {
        setReferralsList(Array.isArray(referralsData) ? referralsData : [referralsData]);
      } else {
        setReferralsList([]);
      }

      // Save to cache
      const cacheData = {
        customers: customersArray,
        servicemen: servicemenArray,
        referrals: hasPermission(PERMISSIONS.REFERRALS.VIEW) && referralsData
          ? (Array.isArray(referralsData) ? referralsData : [referralsData])
          : []
      };
      saveDashboardDataToCache(cacheData);

      // Don't fetch bookings here - only fetch when bookings tab is clicked
      return true;
    } catch (e) {
      // Soft-fail chart data, don't block dashboard
      console.error("Error fetching lists for charts:", e);
      // Don't clear data if we have cached data
      const cached = getCachedDashboardData();
      if (!cached) {
        // Only clear if no cache available
      }
      return false;
    }
  };

  const toDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    // Handle Firestore Timestamp with toDate method
    if (val && typeof val.toDate === 'function') return val.toDate();
    if (typeof val === "number") return new Date(val);
    if (typeof val === "string") {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    // Firestore timestamp formats
    if (val._seconds) return new Date(val._seconds * 1000);
    if (val.seconds) return new Date(val.seconds * 1000);
    if (val.toMillis) return new Date(val.toMillis());
    if (val.createdAt?._seconds) return new Date(val.createdAt._seconds * 1000);
    return null;
  };

  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const computeCounts = (items, dateAccessor) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const startOfWeek = new Date(startOfToday.getTime() - 6 * dayMs);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let today = 0, week = 0, month = 0;
    items.forEach((it) => {
      const dRaw = dateAccessor(it);
      const d = toDate(dRaw) || toDate(it.createdAt) || toDate(it.registeredAt) || toDate(it.lastSeen);
      if (!d) return;
      if (isSameDay(d, now)) today += 1;
      if (d >= startOfWeek) week += 1;
      if (d >= startOfMonth) month += 1;
    });
    return { today, week, month };
  };

  const customerCounts = useMemo(() => computeCounts(customerList, (u) => u.createdAt || u.registeredAt), [customerList]);
  const servicemenCounts = useMemo(() => computeCounts(servicemenList, (s) => s.availabilityStart), [servicemenList]);
  const referralsCounts = useMemo(() => computeCounts(referralsList, (r) => r.createdAt), [referralsList]);
  const bookingsCounts = useMemo(() => computeCounts(bookingsList, (b) => b.bookingDate || b.bookedAt), [bookingsList]);

  // Filter flagged servicemen
  const flaggedServicemenList = useMemo(() => {
    return servicemenList.filter((serviceman) => {
      const isFlagged = serviceman.isFlaged === true || serviceman.isFlaged === 'true' || serviceman.isFlaged === 'True';
      return isFlagged;
    });
  }, [servicemenList]);

  // Calculate servicemen stats from actual data
  const servicemenStats = useMemo(() => {
    const total = servicemenList.length;
    let approved = 0;
    let accountsEdited = 0;

    servicemenList.forEach((serviceman) => {
      // Check if Approved is explicitly true
      if (serviceman.Approved === true || serviceman.Approved === 'true' || serviceman.Approved === 'True') {
        approved++;
      }
      // Check if accountEdited is true (account was edited after creation)
      if (serviceman.accountEdited === true) {
        accountsEdited++;
      }
    });

    // Rejected = Total - Approved
    const rejected = total - approved;

    return {
      total,
      approved,
      rejected,
      accountsEdited
    };
  }, [servicemenList]);

  // XY dotted chart range filter and series
  const [rangeFilter, setRangeFilter] = useState('today'); // today | last7 | month | total
  const [customStartDate, setCustomStartDate] = useState(''); // yyyy-mm-dd
  const [customEndDate, setCustomEndDate] = useState(''); // yyyy-mm-dd

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const getBuckets = (filter) => {
    const now = new Date();
    const buckets = [];
    if (filter === 'today') {
      const base = startOfDay(now);
      for (let h = 0; h < 24; h++) {
        const start = new Date(base.getTime()); start.setHours(h, 0, 0, 0);
        const end = new Date(base.getTime()); end.setHours(h + 1, 0, 0, 0);
        const label = start.toLocaleTimeString([], { hour: 'numeric' });
        buckets.push({ key: `h${h}`, label, start, end });
      }
    } else if (filter === 'last7') {
      const base = startOfDay(now);
      for (let i = 6; i >= 0; i--) {
        const start = new Date(base.getTime() - i * 24 * 60 * 60 * 1000);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        const label = start.toLocaleDateString([], { weekday: 'short' });
        buckets.push({ key: `d${start.toDateString()}`, label, start, end });
      }
    } else if (filter === 'month') {
      const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      for (let d = new Date(startMonth); d < endMonth; d.setDate(d.getDate() + 1)) {
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
        const label = String(start.getDate());
        buckets.push({ key: `m${label}`, label, start, end });
      }
    } else if (filter === 'total') {
      for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        const label = d.toLocaleDateString([], { month: 'short', year: '2-digit' });
        buckets.push({ key: `t${label}`, label, start, end });
      }
    }
    return buckets;
  };

  const bucketize = (items, dateAccessor, filter) => {
    const buckets = getBuckets(filter);
    const enriched = buckets.map((b) => ({ ...b, count: 0, members: [] }));
    items.forEach((it) => {
      const d = toDate(dateAccessor(it)) || toDate(it.createdAt) || toDate(it.registeredAt) || toDate(it.lastSeen);
      if (!d) return;
      for (const b of enriched) {
        if (d >= b.start && d < b.end) {
          b.count += 1;
          b.members.push(it);
          break;
        }
      }
    });
    const max = enriched.reduce((m, b) => Math.max(m, b.count), 0) || 1;
    return { buckets: enriched, max };
  };

  const customerSeries = useMemo(() => bucketize(customerList, (u) => u.createdAt || u.registeredAt, rangeFilter), [customerList, rangeFilter]);
  const servicemenSeries = useMemo(() => bucketize(servicemenList, (s) => s.availabilityStart, rangeFilter), [servicemenList, rangeFilter]);
  const referralsSeries = useMemo(() => bucketize(referralsList, (r) => r.createdAt, rangeFilter), [referralsList, rangeFilter]);
  const bookingsSeries = useMemo(() => bucketize(bookingsList, (b) => b.bookingDate || b.bookedAt, rangeFilter), [bookingsList, rangeFilter]);

  // Compute effective date range for export based on rangeFilter and optional custom dates
  const getEffectiveRange = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const defaultRanges = {
      today: { start: startOfToday, end: new Date(startOfToday.getTime() + dayMs) },
      last7: { start: new Date(startOfToday.getTime() - 6 * dayMs), end: new Date(startOfToday.getTime() + dayMs) },
      month: { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 1) },
      total: { start: new Date(0), end: new Date(8640000000000000) },
    };

    let { start, end } = defaultRanges[rangeFilter] || defaultRanges.total;

    // If last7, month, or total and custom dates provided, override
    if ((rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total')) {
      const hasStart = customStartDate && customStartDate.trim() !== '';
      const hasEnd = customEndDate && customEndDate.trim() !== '';
      if (hasStart) {
        const s = new Date(customStartDate + 'T00:00:00');
        if (!isNaN(s.getTime())) start = s;
      }
      if (hasEnd) {
        const e = new Date(customEndDate + 'T23:59:59.999');
        if (!isNaN(e.getTime())) end = e;
      }
    }
    return { start, end };
  };

  const filterByRange = (items, dateAccessor) => {
    const { start, end } = getEffectiveRange();
    return items.filter((it) => {
      const d = toDate(dateAccessor(it)) || toDate(it.createdAt) || toDate(it.registeredAt) || toDate(it.lastSeen);
      return d && d >= start && d < end;
    });
  };

  const exportToExcel = (rows, filename) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, filename);
  };

  const buildCustomerRows = (items) => {
    return items.map((it) => {
      const createdAtStr = (() => { const d = toDate(it.createdAt) || toDate(it.registeredAt); return d ? d.toLocaleString() : 'N/A'; })();
      const lastSeenStr = (() => { const d = toDate(it.lastSeen); return d ? d.toLocaleString() : 'N/A'; })();
      const address = it.address || it.location || it.city || 'N/A';
      const deviceType = it.deviceType && it.deviceType.trim() !== '' ? it.deviceType : 'N/A';
      return {
        id: it.id || 'N/A',
        gender: (it.gender || '').toString() || 'N/A',
        phone: it.phone || 'N/A',
        dob: it.dob || it.dateOfBirth || 'N/A',
        name: it.name || 'N/A',
        email: it.email || 'N/A',
        created_at: createdAtStr,
        address,
        last_seen: lastSeenStr,
        device_type: deviceType,
      };
    });
  };

  const buildServicemenRows = (items) => {
    return items.map((it) => {
      const availabilityStart = (() => { const d = toDate(it.availabilityStart); return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'; })();
      const availabilityEnd = (() => { const d = toDate(it.availabilityEnd); return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'; })();
      const address = it.address || it.location || it.city || 'N/A';
      const deviceType = it.deviceType && it.deviceType.trim() !== '' ? it.deviceType : 'N/A';
      return {
        id: it.id || 'N/A',
        profession: it.profession || 'N/A',
        availabilityStart,
        availabilityEnd,
        gender: (it.gender || '').toString() || 'N/A',
        phone: it.phone || 'N/A',
        name: it.name || 'N/A',
        email: it.email || 'N/A',
        address,
        primary_area: it.areaName || 'N/A',
        device_type: deviceType,
      };
    });
  };

  const buildReferralsRows = (items) => {
    return items.map((it) => {
      const createdAtStr = (() => { const d = toDate(it.createdAt); return d ? d.toLocaleString() : 'N/A'; })();
      return {
        id: it.id || 'N/A',
        referrerAuthId: it.referrerAuthId || 'N/A',
        newAuthId: it.newAuthId || 'N/A',
        status: it.status || 'N/A',
        reward: it.reward || 'N/A',
        created_at: createdAtStr,
      };
    });
  };

  // Function to fetch bookings from Firebase "Bookings" collection
  const fetchAllBookings = async () => {
    // Prevent concurrent calls using ref
    if (isFetchingBookingsRef.current) {
      return bookingsList;
    }

    try {
      isFetchingBookingsRef.current = true;
      setLoadingBookings(true);

      // Fetch bookings directly from the top-level "Bookings" collection
      const bookingsSnapshot = await getDocs(collection(db, "Bookings"));
      const bookingsData = [];

      bookingsSnapshot.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });

      isFetchingBookingsRef.current = false;
      setLoadingBookings(false);
      return bookingsData;
    } catch (e) {
      isFetchingBookingsRef.current = false;
      setLoadingBookings(false);
      console.error('Error fetching bookings:', e);
      return [];
    }
  };

  // Ref to track if bookings have been loaded
  const bookingsLoadedRef = useRef(false);

  // Function to load bookings with localStorage caching
  const loadBookingsWithCache = useCallback(async () => {
    // Check if bookings are already loaded
    if (bookingsLoadedRef.current) {
      return;
    }

    // Check localStorage first
    const cachedBookings = localStorage.getItem('dashboard_bookings');
    if (cachedBookings) {
      try {
        const parsed = JSON.parse(cachedBookings);
        setBookingsList(parsed);
        bookingsLoadedRef.current = true;
        return;
      } catch (e) {
        console.error('Error parsing cached bookings:', e);
        // If parsing fails, remove invalid cache and fetch fresh
        localStorage.removeItem('dashboard_bookings');
      }
    }

    // If not in cache, fetch from Firebase "Bookings" collection
    try {
      const bookings = await fetchAllBookings();
      setBookingsList(bookings);
      bookingsLoadedRef.current = true;

      // Store in localStorage for caching
      try {
        localStorage.setItem('dashboard_bookings', JSON.stringify(bookings));
      } catch (e) {
        console.error('Error storing bookings in localStorage:', e);
      }
    } catch (e) {
      console.error('Error loading bookings:', e);
    }
  }, []);

  const buildBookingsRows = (items) => {
    return items.map((b) => {
      const bookingDate = toDate(b.bookingDate || b.bookedAt);
      return {
        id: b.bookingId || b.id || 'N/A',
        serviceman_id: b.servicemanId || 'N/A',
        serviceman_name: b.servicemanName || 'N/A',
        customer_id: b.userId || b.uid || 'N/A',
        status: b.status || 'N/A',
        booking_date: bookingDate ? bookingDate.toLocaleString() : 'N/A',
        profession: b.profession || 'N/A',
        amount: b.amount || b.price || b.total || 'N/A',
      };
    });
  };

  const handleExport = async () => {
    const dateSuffix = new Date().toISOString().split('T')[0];
    if (activeTab === 'customers') {
      const filtered = filterByRange(customerList, (u) => u.createdAt || u.registeredAt);
      const rows = buildCustomerRows(filtered);
      exportToExcel(rows, `Customers_${rangeFilter}_${dateSuffix}.xlsx`);
      return;
    }
    if (activeTab === 'servicemen') {
      const filtered = filterByRange(servicemenList, (s) => s.availabilityStart);
      const rows = buildServicemenRows(filtered);
      exportToExcel(rows, `Servicemen_${rangeFilter}_${dateSuffix}.xlsx`);
      return;
    }
    if (activeTab === 'referrals') {
      const filtered = filterByRange(referralsList, (r) => r.createdAt);
      const rows = buildReferralsRows(filtered);
      exportToExcel(rows, `Referrals_${rangeFilter}_${dateSuffix}.xlsx`);
      return;
    }
    if (activeTab === 'bookings') {
      const filtered = filterByRange(bookingsList, (b) => b.bookingDate || b.bookedAt);
      const rows = buildBookingsRows(filtered);
      exportToExcel(rows, `Bookings_${rangeFilter}_${dateSuffix}.xlsx`);
      return;
    }
    if (activeTab === 'flagged') {
      const rows = buildServicemenRows(flaggedServicemenList);
      exportToExcel(rows, `Flagged_Servicemen_${dateSuffix}.xlsx`);
      return;
    }
  };

  const handleUnflag = async (servicemanId) => {
    const confirmMessage = "Are you sure you want to unflag this serviceman? This will remove the flag status.";

    if (!window.confirm(confirmMessage)) return;

    try {
      // Send request to unflag the serviceman
      const updateData = { isFlaged: null };

      const response = await fetch(
        `https://us-central1-workexpress-1ffff.cloudfunctions.net/app/servicemen/${servicemanId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to unflag serviceman");
      }

      // Update local state - remove isFlaged from the serviceman
      setServicemenList((prev) =>
        prev.map((serviceman) => {
          if (serviceman.id === servicemanId) {
            const updated = { ...serviceman };
            delete updated.isFlaged;
            return updated;
          }
          return serviceman;
        })
      );

      alert("Serviceman unflagged successfully");
    } catch (error) {
      console.error("Error unflagging serviceman:", error);
      alert(`Failed to unflag serviceman: ${error.message}`);
    }
  };

  // Chart data generation functions
  const generateLineChartData = (series, color) => {
    return {
      labels: series.buckets.map(b => b.label),
      datasets: [
        {
          label: 'Count',
          data: series.buckets.map(b => b.count),
          borderColor: color,
          backgroundColor: color + '20',
          tension: 0.1,
          fill: false,
        }
      ]
    };
  };

  const generateBarChartData = (counts, colors) => {
    return {
      labels: ['Today', 'Week', 'Month'],
      datasets: [
        {
          label: 'Count',
          data: [counts.today, counts.week, counts.month],
          backgroundColor: colors,
          borderWidth: 0,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        }
      }
    }
  };

  const barChartOptions = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      bar: {
        borderWidth: 0,
      }
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    }
  };

  const getLabelsForFilter = (filter) => getBuckets(filter).map((b) => b.label);

  const filterByPeriod = (items, dateAccessor, period) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const startOfWeek = new Date(startOfToday.getTime() - 6 * dayMs);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return items.filter((it) => {
      const dRaw = dateAccessor(it);
      const d = toDate(dRaw) || toDate(it.createdAt) || toDate(it.registeredAt) || toDate(it.lastSeen);
      if (!d) return false;
      if (period === 'today') return isSameDay(d, now);
      if (period === 'week') return d >= startOfWeek;
      if (period === 'month') return d >= startOfMonth;
      if (period === 'all') return true;
      return false;
    });
  };

  const filterReferralsByPeriod = (period) => {
    return filterByPeriod(referralsList, (r) => r.createdAt, period);
  };

  // Helper function to get serviceman name by auth ID
  const getServicemanNameByAuthId = (authId) => {
    const serviceman = servicemenList.find(s => s.id === authId);
    return serviceman?.name || 'Unknown Serviceman';
  };

  // Helper function to get customer name by auth ID
  const getCustomerNameByAuthId = (authId) => {
    const customer = customerList.find(c => c.id === authId);
    return customer?.name || 'Unknown Customer';
  };

  // Build (x,y) coordinates for non-zero buckets and a path string
  const buildLineGeometry = (series) => {
    const n = series.buckets.length;
    const max = Math.max(1, series.max);
    if (!n) return { points: [], pathD: "" };

    const coords = [];
    for (let i = 0; i < n; i++) {
      const b = series.buckets[i];
      if (b.count <= 0) continue; // skip zeros
      // Calculate x position to align with day labels - use even spacing
      const x = n === 1 ? 0.5 : (i / (n - 1)); // 0 to 1 for even spacing
      const y = 0.95 - (b.count / max) * 0.9; // 0.05..0.95 (invert with padding)
      coords.push({ i, label: b.label, members: b.members, count: b.count, x, y });
    }
    if (coords.length === 0) return { points: [], pathD: "" };

    const pathD = coords
      .map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x * 100} ${p.y * 100}`)
      .join(" ");

    return { points: coords, pathD };
  };

  const openModal = (title, items) => {
    setModalTitle(title);
    setModalItems(items);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    const storedUsername = localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername);
    }

    // Clear bookings from localStorage on page refresh
    localStorage.removeItem('dashboard_bookings');
    bookingsLoadedRef.current = false;

    // Load cached data immediately for instant display
    const cachedStats = getCachedStats();
    const cachedData = getCachedDashboardData();

    if (cachedStats && cachedData) {
      // We have cached data, show it immediately
      setStats(cachedStats);
      setCustomerList(cachedData.customers || []);
      setServicemenList(cachedData.servicemen || []);
      setReferralsList(cachedData.referrals || []);
      setLoading(false);
      setError(null);

      // Fetch fresh data in background without showing loader
      fetchDashboardStats(false);
      fetchCustomersAndServicemen(false);
    } else {
      // No cache or partial cache, fetch with loader
      setLoading(true);
      setError(null);

      const loadData = async () => {
        try {
          // If we have partial cache, use it
          if (cachedStats) {
            setStats(cachedStats);
          }
          if (cachedData) {
            setCustomerList(cachedData.customers || []);
            setServicemenList(cachedData.servicemen || []);
            setReferralsList(cachedData.referrals || []);
          }

          // Ensure minimum display time for loader (300ms)
          const minDisplayTime = new Promise(resolve => setTimeout(resolve, 300));

          // Wait for both fetches to complete
          const fetchData = Promise.all([
            cachedStats ? Promise.resolve(true) : fetchDashboardStats(true),
            cachedData ? Promise.resolve(true) : fetchCustomersAndServicemen(true)
          ]);

          // Wait for both minimum display time and data fetch
          await Promise.all([minDisplayTime, fetchData]);

          setLoading(false);
        } catch (error) {
          console.error("Error loading dashboard data:", error);
          // Still wait minimum time even on error
          await new Promise(resolve => setTimeout(resolve, 300));
          setLoading(false);
        }
      };

      loadData();
    }

    // Only refresh stats periodically, not the full data (to reduce API calls)
    const intervalId = setInterval(() => {
      fetchDashboardStats(false); // Background refresh
    }, 30000);

    // Refresh full data less frequently
    const dataRefreshInterval = setInterval(() => {
      fetchCustomersAndServicemen(false); // Background refresh
    }, 60000); // Refresh every minute instead of every 10 seconds

    return () => {
      clearInterval(intervalId);
      clearInterval(dataRefreshInterval);
    };
  }, []);

  // Load bookings when bookings tab is clicked
  useEffect(() => {
    if (activeTab === 'bookings') {
      loadBookingsWithCache();
    }
  }, [activeTab, loadBookingsWithCache]);

  if (loading) {
    return (
      <>
        <div className="loading-overlay">
          <span className="loader"></span>
        </div>
        <style>{`
          .loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100vw;
            height: 100vh;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: rgba(255, 255, 255, 0.95);
            z-index: 6;
            color: var(--text-secondary, #666);
          }
          
          [data-theme="dark"] .loading-overlay {
            background-color: rgba(0, 0, 0, 0.95);
          }

          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #000;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          
          [data-theme="dark"] .loader::after {
            border: 2px solid #FFF;
          }
          
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </>
    );
  }

  if (loadingBookings) {
    return (
      <div className="loading-container">
        <span className="loader"></span>
      </div>
    );
  }

  if (error) {
    return <div className="error-container">Error: {error}</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Hello {username || "User"}</h1>
        <div className="dashboard-date">{new Date().toLocaleDateString()}</div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "customers" ? "active" : ""}`}
          onClick={() => setActiveTab("customers")}
        >
          <FaUsers className="tab-icon" />
          Customers
        </button>
        <button
          className={`tab-button ${activeTab === "servicemen" ? "active" : ""}`}
          onClick={() => setActiveTab("servicemen")}
        >
          <FaUserTie className="tab-icon" />
          Servicemen
        </button>
        <button
          className={`tab-button ${activeTab === "bookings" ? "active" : ""}`}
          onClick={() => setActiveTab("bookings")}
        >
          <FaClipboardList className="tab-icon" />
          Bookings
        </button>
        <button
          className={`tab-button ${activeTab === "referrals" ? "active" : ""}`}
          onClick={() => setActiveTab("referrals")}
        >
          <FaCalendar className="tab-icon" />
          Referrals
        </button>
        <button
          className={`tab-button ${activeTab === "flagged" ? "active" : ""}`}
          onClick={() => setActiveTab("flagged")}
        >
          <FaFlag className="tab-icon" />
          Flagged
        </button>
      </div>
      {/* Tab Content */}
      <div className="tab-content">
        {/* Customers Tab */}
        {activeTab === "customers" && (
          <div className="tab-panel">
            <div className="stats-grid">
              {/* Today Customers */}
              <div className="stat-card" style={{ '--card-color': '#3b82f6' }}>
                <FaUsers className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Customers - Today',
                        filterByPeriod(customerList, (u) => u.createdAt || u.registeredAt, 'today')
                      )
                    }
                  >
                    {customerCounts.today || 0}
                  </p>
                  <p className="stat-label">Customers Today</p>
                </div>
              </div>

              {/* This Week Customers */}
              <div className="stat-card" style={{ '--card-color': '#8b5cf6' }}>
                <FaUsers className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Customers - This Week',
                        filterByPeriod(customerList, (u) => u.createdAt || u.registeredAt, 'week')
                      )
                    }
                  >
                    {customerCounts.week || 0}
                  </p>
                  <p className="stat-label">Customers This Week</p>
                </div>
              </div>

              {/* This Month Customers */}
              <div className="stat-card" style={{ '--card-color': '#16a34a' }}>
                <FaUsers className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Customers - This Month',
                        filterByPeriod(customerList, (u) => u.createdAt || u.registeredAt, 'month')
                      )
                    }
                  >
                    {customerCounts.month || 0}
                  </p>
                  <p className="stat-label">Customers This Month</p>
                </div>
              </div>

              {/* Total Customers */}
              <div className="stat-card" style={{ '--card-color': '#0f172a' }}>
                <FaUsers className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'All Customers',
                        filterByPeriod(customerList, (u) => u.createdAt || u.registeredAt, 'all')
                      )
                    }
                  >
                    {stats?.totalCustomers || 0}
                  </p>
                  <p className="stat-label">Total Customers</p>
                </div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-card">
                <h3 className="chart-title">Customers Registrations</h3>
                <div className="chart-container">
                  <Bar
                    data={generateBarChartData(customerCounts, ['#3b82f6', '#8b5cf6', '#16a34a'])}
                    options={{
                      ...barChartOptions,
                      onClick: (event, elements) => {
                        if (elements.length > 0) {
                          const index = elements[0].index;
                          const periods = ['today', 'week', 'month'];
                          const titles = ['Customers - Today', 'Customers - This Week', 'Customers - This Month'];
                          openModal(titles[index], filterByPeriod(customerList, (u) => u.createdAt || u.registeredAt, periods[index]));
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="xy-section">
              <div className="xy-header">
                <h3 className="xy-title">Customer Activity Timeline</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {(rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total') && (
                    <>
                      <input type="date" className="xy-filter" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                      <input type="date" className="xy-filter" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                    </>
                  )}
                  <select className="xy-filter" value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)}>
                    <option value="today">Today</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="month">This Month</option>
                    <option value="total">Total</option>
                  </select>
                  <button className="export-btn" onClick={handleExport}>Export</button>
                </div>
              </div>

              <div className="xy-grid">
                <div className="xy-card">
                  <div className="xy-card-title">Customers</div>
                  <div className="chart-container">
                    <Line
                      data={generateLineChartData(customerSeries, '#3b82f6')}
                      options={{
                        ...chartOptions,
                        onClick: (event, elements) => {
                          if (elements.length > 0) {
                            const index = elements[0].index;
                            const bucket = customerSeries.buckets[index];
                            openModal(`Customers - ${bucket.label}`, bucket.members);
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Servicemen Tab */}
        {activeTab === "servicemen" && (
          <div className="tab-panel">
            <div className="stats-grid">
              <div className="stat-card" style={{ '--card-color': '#f59e0b' }}>
                <FaEdit className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Servicemen - Accounts Edited',
                        servicemenList.filter((s) => s.accountEdited === true)
                      )
                    }
                  >
                    {servicemenStats.accountsEdited || 0}
                  </p>
                  <p className="stat-label">Accounts Edited</p>
                </div>
              </div>
              <div className="stat-card" style={{ '--card-color': '#3b82f6' }}>
                <FaClock className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Servicemen - Today',
                        filterByPeriod(servicemenList, (s) => s.availabilityStart, 'today')
                      )
                    }
                  >
                    {servicemenCounts.today || 0}
                  </p>
                  <p className="stat-label">Today Servicemen</p>
                </div>
              </div>
              <div className="stat-card" style={{ '--card-color': '#8b5cf6' }}>
                <FaUserTie className="stat-icon" />
                <div className="stat-info">
                  <p className="stat-value" style={{ cursor: 'pointer' }} onClick={() => openModal('All Servicemen', filterByPeriod(servicemenList, (s) => s.availabilityStart, 'all'))}>{servicemenStats.total}</p>
                  <p className="stat-label">Total Servicemen</p>
                </div>
              </div>
              <div className="stat-card" style={{ '--card-color': '#16a34a' }}>
                <FaCheck className="stat-icon" />
                <div className="stat-info">
                  <p className="stat-value">{servicemenStats.approved}</p>
                  <p className="stat-label">Approved Servicemen</p>
                </div>
              </div>
              <div className="stat-card" style={{ '--card-color': '#dc2626' }}>
                <FaTimes className="stat-icon" />
                <div className="stat-info">
                  <p className="stat-value">{servicemenStats.rejected}</p>
                  <p className="stat-label">Rejected Servicemen</p>
                </div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-card">
                <h3 className="chart-title">Servicemen Registrations</h3>
                <div className="chart-container">
                  <Bar
                    data={generateBarChartData(servicemenCounts, ['#ef4444', '#f59e0b', '#06b6d4'])}
                    options={{
                      ...barChartOptions,
                      onClick: (event, elements) => {
                        if (elements.length > 0) {
                          const index = elements[0].index;
                          const periods = ['today', 'week', 'month'];
                          const titles = ['Servicemen - Today', 'Servicemen - This Week', 'Servicemen - This Month'];
                          openModal(titles[index], filterByPeriod(servicemenList, (s) => s.availabilityStart, periods[index]));
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="xy-section">
              <div className="xy-header">
                <h3 className="xy-title">Servicemen Activity Timeline</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {(rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total') && (
                    <>
                      <input type="date" className="xy-filter" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                      <input type="date" className="xy-filter" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                    </>
                  )}
                  <select className="xy-filter" value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)}>
                    <option value="today">Today</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="month">This Month</option>
                    <option value="total">Total</option>
                  </select>
                  <button className="export-btn" onClick={handleExport}>Export</button>
                </div>
              </div>

              <div className="xy-grid">
                <div className="xy-card">
                  <div className="xy-card-title">Servicemen</div>
                  <div className="chart-container">
                    <Line
                      data={generateLineChartData(servicemenSeries, '#ef4444')}
                      options={{
                        ...chartOptions,
                        onClick: (event, elements) => {
                          if (elements.length > 0) {
                            const index = elements[0].index;
                            const bucket = servicemenSeries.buckets[index];
                            openModal(`Servicemen - ${bucket.label}`, bucket.members);
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="tab-panel">
            {/* Bookings summary cards */}
            <div className="stats-grid">
              {/* Today Bookings */}
              <div className="stat-card" style={{ '--card-color': '#d97706' }}>
                <FaClipboardList className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Bookings - Today',
                        filterByPeriod(bookingsList, (b) => b.bookingDate || b.bookedAt, 'today')
                      )
                    }
                  >
                    {bookingsCounts.today || 0}
                  </p>
                  <p className="stat-label">Bookings Today</p>
                </div>
              </div>

              {/* This Week Bookings */}
              <div className="stat-card" style={{ '--card-color': '#0891b2' }}>
                <FaClipboardList className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Bookings - This Week',
                        filterByPeriod(bookingsList, (b) => b.bookingDate || b.bookedAt, 'week')
                      )
                    }
                  >
                    {bookingsCounts.week || 0}
                  </p>
                  <p className="stat-label">Bookings This Week</p>
                </div>
              </div>

              {/* This Month Bookings */}
              <div className="stat-card" style={{ '--card-color': '#16a34a' }}>
                <FaClipboardList className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'Bookings - This Month',
                        filterByPeriod(bookingsList, (b) => b.bookingDate || b.bookedAt, 'month')
                      )
                    }
                  >
                    {bookingsCounts.month || 0}
                  </p>
                  <p className="stat-label">Bookings This Month</p>
                </div>
              </div>

              {/* Total Bookings */}
              <div className="stat-card" style={{ '--card-color': '#0f172a' }}>
                <FaClipboardList className="stat-icon" />
                <div className="stat-info">
                  <p
                    className="stat-value"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      openModal(
                        'All Bookings',
                        filterByPeriod(bookingsList, (b) => b.bookingDate || b.bookedAt, 'all')
                      )
                    }
                  >
                    {bookingsList.length || 0}
                  </p>
                  <p className="stat-label">Total Bookings</p>
                </div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-card">
                <h3 className="chart-title">Bookings Overview</h3>
                <div className="chart-container">
                  <Bar
                    data={generateBarChartData(bookingsCounts, ['#d97706', '#0891b2', '#16a34a'])}
                    options={{
                      ...barChartOptions,
                      onClick: (event, elements) => {
                        if (elements.length > 0) {
                          const index = elements[0].index;
                          const periods = ['today', 'week', 'month'];
                          const titles = ['Bookings - Today', 'Bookings - This Week', 'Bookings - This Month'];
                          openModal(titles[index], filterByPeriod(bookingsList, (b) => b.bookingDate || b.bookedAt, periods[index]));
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="xy-section">
              <div className="xy-header">
                <h3 className="xy-title">Bookings Activity Timeline</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {(rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total') && (
                    <>
                      <input type="date" className="xy-filter" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                      <input type="date" className="xy-filter" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                    </>
                  )}
                  <select className="xy-filter" value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)}>
                    <option value="today">Today</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="month">This Month</option>
                    <option value="total">Total</option>
                  </select>
                  <button className="export-btn" onClick={handleExport}>Export</button>
                </div>
              </div>

              <div className="xy-grid">
                <div className="xy-card">
                  <div className="xy-card-title">Bookings</div>
                  <div className="chart-container">
                    <Line
                      data={generateLineChartData(bookingsSeries, '#d97706')}
                      options={{
                        ...chartOptions,
                        onClick: (event, elements) => {
                          if (elements.length > 0) {
                            const index = elements[0].index;
                            const bucket = bookingsSeries.buckets[index];
                            openModal(`Bookings - ${bucket.label}`, bucket.members);
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <div className="tab-panel">
            {hasPermission(PERMISSIONS.REFERRALS.VIEW) ? (
              <>
                {/* Referrals summary cards */}
                <div className="stats-grid">
                  {/* Today Referrals */}
                  <div className="stat-card" style={{ '--card-color': '#8b5cf6' }}>
                    <FaCalendar className="stat-icon" />
                    <div className="stat-info">
                      <p
                        className="stat-value"
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          openModal('Referrals - Today', filterReferralsByPeriod('today'))
                        }
                      >
                        {referralsCounts.today || 0}
                      </p>
                      <p className="stat-label">Referrals Today</p>
                    </div>
                  </div>

                  {/* This Week Referrals */}
                  <div className="stat-card" style={{ '--card-color': '#f59e0b' }}>
                    <FaCalendar className="stat-icon" />
                    <div className="stat-info">
                      <p
                        className="stat-value"
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          openModal('Referrals - This Week', filterReferralsByPeriod('week'))
                        }
                      >
                        {referralsCounts.week || 0}
                      </p>
                      <p className="stat-label">Referrals This Week</p>
                    </div>
                  </div>

                  {/* This Month Referrals */}
                  <div className="stat-card" style={{ '--card-color': '#06b6d4' }}>
                    <FaCalendar className="stat-icon" />
                    <div className="stat-info">
                      <p
                        className="stat-value"
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          openModal('Referrals - This Month', filterReferralsByPeriod('month'))
                        }
                      >
                        {referralsCounts.month || 0}
                      </p>
                      <p className="stat-label">Referrals This Month</p>
                    </div>
                  </div>

                  {/* Total Referrals */}
                  <div className="stat-card" style={{ '--card-color': '#0f172a' }}>
                    <FaCalendar className="stat-icon" />
                    <div className="stat-info">
                      <p
                        className="stat-value"
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          openModal('All Referrals', filterReferralsByPeriod('all'))
                        }
                      >
                        {referralsList.length || 0}
                      </p>
                      <p className="stat-label">Total Referrals</p>
                    </div>
                  </div>
                </div>

                <div className="charts-grid">
                  <div className="chart-card">
                    <h3 className="chart-title">Referrals</h3>
                    <div className="chart-container">
                      <Bar
                        data={generateBarChartData(referralsCounts, ['#8b5cf6', '#f59e0b', '#06b6d4'])}
                        options={{
                          ...barChartOptions,
                          onClick: (event, elements) => {
                            if (elements.length > 0) {
                              const index = elements[0].index;
                              const periods = ['today', 'week', 'month'];
                              const titles = ['Referrals - Today', 'Referrals - This Week', 'Referrals - This Month'];
                              openModal(titles[index], filterReferralsByPeriod(periods[index]));
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="xy-section">
                  <div className="xy-header">
                    <h3 className="xy-title">Referrals Activity Timeline</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {(rangeFilter === 'last7' || rangeFilter === 'month' || rangeFilter === 'total') && (
                        <>
                          <input type="date" className="xy-filter" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                          <input type="date" className="xy-filter" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                        </>
                      )}
                      <select className="xy-filter" value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)}>
                        <option value="today">Today</option>
                        <option value="last7">Last 7 Days</option>
                        <option value="month">This Month</option>
                        <option value="total">Total</option>
                      </select>
                      <button className="export-btn" onClick={handleExport}>Export</button>
                    </div>
                  </div>

                  <div className="xy-grid">
                    <div className="xy-card">
                      <div className="xy-card-title">Referrals</div>
                      <div className="chart-container">
                        <Line
                          data={generateLineChartData(referralsSeries, '#8b5cf6')}
                          options={{
                            ...chartOptions,
                            onClick: (event, elements) => {
                              if (elements.length > 0) {
                                const index = elements[0].index;
                                const bucket = referralsSeries.buckets[index];
                                openModal(`Referrals - ${bucket.label}`, bucket.members);
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="tab-placeholder">
                <h3>Access Restricted</h3>
                <p>You don't have permission to view referrals data.</p>
              </div>
            )}
          </div>
        )}

        {/* Flagged Tab */}
        {activeTab === "flagged" && (
          <div className="tab-panel">
            <div className="stats-grid">
              <div className="stat-card" style={{ '--card-color': '#f59e0b' }}>
                <FaFlag className="stat-icon" />
                <div className="stat-info">
                  <p className="stat-value" style={{ cursor: 'pointer' }} onClick={() => openModal('All Flagged Servicemen', flaggedServicemenList)}>
                    {flaggedServicemenList.length}
                  </p>
                  <p className="stat-label">Flagged Servicemen</p>
                </div>
              </div>
            </div>

            <div className="charts-grid">
              <div className="chart-card">
                <h3 className="chart-title">Flagged Servicemen List</h3>
                {flaggedServicemenList.length > 0 ? (
                  <div className="chart-container" style={{ padding: 0, height: 'auto' }}>
                    <div className="table-wrap flagged-table-scroll">
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Profession</th>
                            <th>Phone</th>
                            <th>Email</th>
                            <th>Location</th>
                            <th>Main Area</th>
                            <th>Rating</th>
                            <th>Device Type</th>
                            <th>Approved</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flaggedServicemenList.map((serviceman, idx) => {
                            const address = serviceman.address || serviceman.location || serviceman.city || 'N/A';
                            const deviceType = serviceman.deviceType && serviceman.deviceType.trim() !== '' ? serviceman.deviceType : 'N/A';
                            return (
                              <tr key={serviceman.id || idx} className="flagged-table-row">
                                <td>{serviceman.name || 'N/A'}</td>
                                <td>{serviceman.profession || 'N/A'}</td>
                                <td>{serviceman.phone || 'N/A'}</td>
                                <td>{serviceman.email || 'N/A'}</td>
                                <td>{address}</td>
                                <td>{serviceman.areaName || 'N/A'}</td>
                                <td>{serviceman.avgRating ? `${serviceman.avgRating}/5` : 'N/A'}</td>
                                <td>{deviceType}</td>
                                <td>{serviceman.Approved ? 'Yes' : 'No'}</td>
                                <td>
                                  <button
                                    onClick={() => handleUnflag(serviceman.id)}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: '#f59e0b',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                                    title="Unflag Serviceman"
                                  >
                                    Unflag
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="tab-placeholder">
                    <h3>No Flagged Servicemen</h3>
                    <p>There are currently no flagged servicemen.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Drilldown modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalTitle}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {modalItems && modalItems.length > 0 ? (
                (modalTitle.startsWith('Customers') || modalTitle === 'All Customers') ? (
                  <div className="table-wrap">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>id</th>
                          <th>gender</th>
                          <th>phone</th>
                          <th>dob</th>
                          <th>name</th>
                          <th>email</th>
                          <th>created at</th>
                          <th>address</th>
                          <th>last seen</th>
                          <th>device type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItems.map((it, idx) => {
                          const createdAtStr = (() => { const d = toDate(it.createdAt) || toDate(it.registeredAt); return d ? d.toLocaleString() : 'N/A'; })();
                          const lastSeenStr = (() => { const d = toDate(it.lastSeen); return d ? d.toLocaleString() : 'N/A'; })();
                          const address = it.address || it.location || it.city || 'N/A';
                          const deviceType = it.deviceType && it.deviceType.trim() !== '' ? it.deviceType : 'N/A';
                          return (
                            <tr key={idx}>
                              <td>{it.id || 'N/A'}</td>
                              <td>{(it.gender || '').toString() || 'N/A'}</td>
                              <td>{it.phone || 'N/A'}</td>
                              <td>{it.dob || it.dateOfBirth || 'N/A'}</td>
                              <td>{it.name || 'N/A'}</td>
                              <td>{it.email || 'N/A'}</td>
                              <td>{createdAtStr}</td>
                              <td>{address}</td>
                              <td>{lastSeenStr}</td>
                              <td>{deviceType}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (modalTitle.startsWith('Servicemen') || modalTitle === 'All Servicemen') ? (
                  <div className="table-wrap">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>id</th>
                          <th>profession</th>
                          <th>availabilityStart</th>
                          <th>availabilityEnd</th>
                          <th>gender</th>
                          <th>phone</th>
                          <th>name</th>
                          <th>email</th>
                          <th>address</th>
                          <th>primary area</th>
                          <th>device type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItems.map((it, idx) => {
                          const availabilityStart = (() => { const d = toDate(it.availabilityStart); return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'; })();
                          const availabilityEnd = (() => { const d = toDate(it.availabilityEnd); return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'; })();
                          const address = it.address || it.location || it.city || 'N/A';
                          const deviceType = it.deviceType && it.deviceType.trim() !== '' ? it.deviceType : 'N/A';
                          return (
                            <tr key={idx}>
                              <td>{it.id || 'N/A'}</td>
                              <td>{it.profession || 'N/A'}</td>
                              <td>{availabilityStart}</td>
                              <td>{availabilityEnd}</td>
                              <td>{(it.gender || '').toString() || 'N/A'}</td>
                              <td>{it.phone || 'N/A'}</td>
                              <td>{it.name || 'N/A'}</td>
                              <td>{it.email || 'N/A'}</td>
                              <td>{address}</td>
                              <td>{it.areaName || 'N/A'}</td>
                              <td>{deviceType}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (modalTitle.startsWith('Referrals') || modalTitle === 'All Referrals') ? (
                  <div className="table-wrap">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Referrer</th>
                          <th>Referred To</th>
                          <th>Status</th>
                          <th>Reward</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItems.map((it, idx) => {
                          const createdAtStr = (() => { const d = toDate(it.createdAt); return d ? d.toLocaleString() : 'N/A'; })();
                          const referrerName = getServicemanNameByAuthId(it.referrerAuthId);
                          const referredName = getServicemanNameByAuthId(it.newAuthId);
                          return (
                            <tr key={idx}>
                              <td>{it.id || 'N/A'}</td>
                              <td>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{referrerName}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {it.referrerAuthId}</div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{referredName}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {it.newAuthId}</div>
                                </div>
                              </td>
                              <td>{it.status || 'N/A'}</td>
                              <td>{it.reward ? `₹${it.reward}` : 'N/A'}</td>
                              <td>{createdAtStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (modalTitle.startsWith('Bookings') || modalTitle === 'All Bookings') ? (
                  <div className="table-wrap">
                    <table className="items-table">
                      <thead>
                        <tr>
                          <th>Booking ID</th>
                          <th>Booking Date & Time</th>
                          <th>Profession</th>
                          <th>Serviceman</th>
                          <th>Customer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItems.map((it, idx) => {
                          const bookingDate = toDate(it.bookingDate || it.bookedAt);
                          const bookingDateStr = bookingDate ?
                            `${bookingDate.toLocaleDateString()} ${bookingDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}` :
                            'N/A';
                          const servicemanId = it.servicemanId || it.sid;
                          const serviceman = servicemenList.find(s => s.id === servicemanId);
                          const servicemanName = serviceman?.name || it.servicemanName || 'Unknown Serviceman';
                          const customerId = it.userId || it.uid;
                          const customer = customerList.find(c => c.id === customerId);
                          const customerName = customer?.name || 'Unknown Customer';
                          return (
                            <tr key={idx}>
                              <td>{it.bookingId || it.id || 'N/A'}</td>
                              <td>{bookingDateStr}</td>
                              <td>{it.profession || 'N/A'}</td>
                              <td>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{servicemanName}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {servicemanId || 'N/A'}</div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{customerName}</div>
                                  <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {customerId || 'N/A'}</div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="items-list">
                    {modalItems.map((it, idx) => (
                      <div key={idx} className="item-card">
                        <div className="item-title">{it.name || it.username || it.email || it.phone || 'Unnamed'}</div>
                        <pre className="item-json">{JSON.stringify(it, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="empty">No records found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .dashboard-container {
          padding: 2.5rem;
          background: var(--bg-primary, linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%));
          min-height: 100vh;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          box-sizing: border-box;
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .dashboard-title {
          font-size: 2.25rem;
          font-weight: 700;
          color: var(--text-primary, #1f2937);
          margin: 0;
        }

        [data-theme="dark"] .dashboard-title {
          background: linear-gradient(to right, var(--text-primary), var(--text-secondary));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .dashboard-date {
          font-size: 1rem;
          color: var(--text-secondary, #6b7280);
          font-weight: 500;
          padding: 0.5rem 1rem;
          background: var(--bg-card, #ffffff);
          border-radius: 20px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0, 0, 0, 0.05));
        }

        /* Tab Navigation Styles */
        .tab-navigation {
          display: flex;
          background: var(--bg-card, #ffffff);
          border-radius: 16px;
          padding: 0.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 12px var(--shadow, rgba(0, 0, 0, 0.08));
          gap: 0.5rem;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          box-sizing: border-box;
          flex-wrap: nowrap;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 1.5rem;
          border: none;
          background: transparent;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-muted, #6b7280);
          transition: all 0.3s ease;
          flex: 1 1 auto;
          justify-content: center;
          min-width: 0;
          white-space: nowrap;
        }

        .tab-button:hover {
          background: var(--bg-hover, #f3f4f6);
          color: var(--text-primary, #374151);
          transform: translateY(-2px);
        }

        .tab-button.active {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .tab-button.active:hover {
          background: linear-gradient(135deg, #2563eb, #1e40af);
          transform: translateY(-2px);
        }

        .tab-icon {
          font-size: 1.1rem;
        }

        /* Tab Content Styles */
        .tab-content {
          min-height: 400px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
          overflow-y: visible;
        }

        .tab-panel {
          animation: fadeIn 0.3s ease-in-out;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
          overflow-y: visible;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .tab-placeholder {
          background: var(--bg-card, #ffffff);
          border-radius: 16px;
          padding: 3rem;
          text-align: center;
          box-shadow: 0 6px 15px var(--shadow, rgba(0, 0, 0, 0.08));
          margin-top: 2rem;
        }

        .tab-placeholder h3 {
          color: var(--text-primary, #1f2937);
          font-size: 1.5rem;
          margin-bottom: 1rem;
          font-weight: 700;
        }

        .tab-placeholder p {
          color: var(--text-muted, #6b7280);
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 0.5rem;
        }

        /* Responsive Tab Navigation */
        @media (max-width: 768px) {
          .dashboard-container {
            padding: 1rem;
          }
          
          .dashboard-header {
            margin-bottom: 1.5rem;
          }
          
          .dashboard-title {
            font-size: 1.5rem;
          }
          
          .tab-navigation {
            gap: 0.25rem;
            padding: 0.25rem;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
          }
          
          .tab-button {
            padding: 0.75rem 1rem;
            font-size: 0.875rem;
            flex: 0 0 auto;
            min-width: fit-content;
          }
          
          .tab-icon {
            font-size: 0.9rem;
          }
        }
        
        @media (max-width: 480px) {
          .tab-button {
            padding: 0.6rem 0.75rem;
            font-size: 0.8rem;
            gap: 0.25rem;
          }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow-x: hidden;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-top: 2.5rem;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }
        .xy-section { margin-top: 2.5rem; width: 100%; max-width: 100%; box-sizing: border-box; }
        .xy-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; flex-wrap: wrap; gap: 0.5rem; }
        .xy-title { margin: 0; font-size: 1.1rem; color: var(--text-primary, #1f2937); font-weight: 700; }
        .xy-filter { padding: 8px 12px; border: 1px solid var(--border-color, #d1d5db); border-radius: 6px; background: var(--bg-card, #fff); color: var(--text-primary, #374151); }
        .xy-grid { display: grid; grid-template-columns: 1fr; gap: 1.25rem; width: 100%; max-width: 100%; box-sizing: border-box; }
        .xy-card { background: var(--bg-card, #fff); border-radius: 0; box-shadow: 0 6px 15px var(--shadow, rgba(0,0,0,0.07)); padding: 14px; width: 100%; max-width: 100%; box-sizing: border-box; }
        .xy-card-title { font-weight: 600; color: var(--text-primary, #111827); margin-bottom: 8px; }
        .xy-plot { display: flex; gap: 8px; }
        .xy-ylabels { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; font-size: 12px; color: var(--text-muted, #6b7280); padding-right: 6px; }
        .xy-canvas { position: relative; flex: 1; height: 180px; border-left: 1px solid var(--border-color, #e5e7eb); border-bottom: 1px solid var(--border-color, #e5e7eb); padding: 8px; }
        .xy-hline { position: absolute; left: 8px; right: 8px; height: 1px; background: var(--border-color, #e5e7eb); }
        .xy-xlabels { position: absolute; left: 8px; right: 8px; bottom: -18px; display: flex; justify-content: space-between; font-size: 11px; color: var(--text-muted, #6b7280); }
        
        @media (max-width: 768px) {
          .charts-grid {
            grid-template-columns: 1fr;
            margin-top: 1.5rem;
          }
          .xy-grid {
            grid-template-columns: 1fr;
          }
          .bar-chart {
            gap: 1rem;
            padding: 0.5rem 0.5rem;
          }
          .bar-item {
            width: 30%;
          }
        }

        /* New SVG overlay for line + non-zero dots */
        .xy-svg {
          position: absolute;
          top: 8px;
          left: 8px;
          right: 8px;
          bottom: 8px;
          width: calc(100% - 16px);
          height: calc(100% - 16px);
          pointer-events: auto;
          aspect-ratio: 1;
        }

        .chart-card {
          background: var(--bg-card, #ffffff);
          border-radius: 0;
          padding: 1.5rem;
          box-shadow: 0 6px 15px var(--shadow, rgba(0, 0, 0, 0.08));
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }
        .chart-container {
          height: 300px;
          width: 100%;
          position: relative;
          max-width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }
        .chart-container .table-wrap {
          height: 100%;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow-x: auto;
          overflow-y: auto;
        }
        
        /* Responsive height for flagged table */
        .flagged-table-scroll {
          max-height: calc(100vh - 350px);
          min-height: 400px;
        }
        
        @media (max-width: 1024px) {
          .flagged-table-scroll {
            max-height: calc(100vh - 300px);
            min-height: 350px;
          }
        }
        
        @media (max-width: 768px) {
          .flagged-table-scroll {
            max-height: calc(100vh - 280px);
            min-height: 300px;
          }
        }
        
        @media (max-width: 480px) {
          .flagged-table-scroll {
            max-height: calc(100vh - 250px);
            min-height: 250px;
          }
        }
        
        /* Beautiful Scrollbar for Chart Container */
        .chart-container::-webkit-scrollbar,
        .chart-container .table-wrap::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        
        .chart-container::-webkit-scrollbar-track,
        .chart-container .table-wrap::-webkit-scrollbar-track {
          background: var(--bg-tertiary, #f1f5f9);
          border-radius: 10px;
        }
        
        .chart-container::-webkit-scrollbar-thumb,
        .chart-container .table-wrap::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #cbd5e1, #94a3b8);
          border-radius: 10px;
          border: 2px solid var(--bg-tertiary, #f1f5f9);
          transition: background 0.3s ease;
        }
        
        .chart-container::-webkit-scrollbar-thumb:hover,
        .chart-container .table-wrap::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #94a3b8, #64748b);
        }
        
        [data-theme="dark"] .chart-container::-webkit-scrollbar-track,
        [data-theme="dark"] .chart-container .table-wrap::-webkit-scrollbar-track {
          background: #1e293b;
        }
        
        [data-theme="dark"] .chart-container::-webkit-scrollbar-thumb,
        [data-theme="dark"] .chart-container .table-wrap::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #475569, #334155);
          border: 2px solid #1e293b;
        }
        
        [data-theme="dark"] .chart-container::-webkit-scrollbar-thumb:hover,
        [data-theme="dark"] .chart-container .table-wrap::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #334155, #1e293b);
        }
        
        .chart-container,
        .chart-container .table-wrap {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 var(--bg-tertiary, #f1f5f9);
        }
        
        [data-theme="dark"] .chart-container,
        [data-theme="dark"] .chart-container .table-wrap {
          scrollbar-color: #475569 #1e293b;
        }
        .chart-container[style*="height: auto"] {
          height: auto;
          min-height: 300px;
          display: flex;
          flex-direction: column;
        }
        
        .chart-container[style*="height: auto"] .table-wrap {
          flex: 1;
        }
        .chart-title {
          margin: 0 0 1rem 0;
          font-size: 1.1rem;
          color: var(--text-primary, #1f2937);
          font-weight: 600;
        }
        .bar-chart {
          display: flex;
          align-items: flex-end;
          gap: 2rem;
          height: 140px;
          padding: 0.5rem 1rem;
          width: 100%;
          justify-content: center;
        }
        .bar-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.35rem;
          width: 30%;
        }
        .bar {
          width: 100%;
          border-radius: 0;
          transition: height 0.3s ease;
        }
        .bar.clickable { cursor: pointer; }
        .bar-label {
          font-size: 0.8rem;
          color: var(--text-muted, #6b7280);
        }
        .bar-value {
          font-size: 0.9rem;
          color: var(--text-primary, #111827);
          font-weight: 600;
        }

        .stat-card {
          background: var(--bg-card, #ffffff);
          border-radius: 16px;
          padding: 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          box-shadow: 0 6px 15px var(--shadow, rgba(0, 0, 0, 0.08));
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 6px;
          height: 100%;
          background: var(--card-color);
          opacity: 0.8;
          transition: width 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }

        .stat-card:hover::before {
          width: 12px;
        }

        .stat-icon {
          font-size: 3rem;
          color: var(--card-color);
          background: rgba(var(--card-color), 0.1);
          padding: 0.75rem;
          border-radius: 12px;
          transition: transform 0.3s ease;
        }

        .stat-card:hover .stat-icon {
          transform: scale(1.15) rotate(5deg);
        }

        .stat-info {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--text-primary, #1f2937);
          margin: 0;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 1.1rem;
          color: var(--text-muted, #6b7280);
          margin: 0;
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: rgba(255, 255, 255, 0.95);
          z-index: 6;
          color: var(--text-secondary, #666);
        }
        
        [data-theme="dark"] .loading-overlay {
          background-color: rgba(0, 0, 0, 0.95);
        }

        .loader {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: inline-block;
          position: relative;
          border: 2px solid #FF3D00;
          box-sizing: border-box;
          animation: rotation 1s linear infinite;
        }
        .loader::after {
          content: '';  
          box-sizing: border-box;
          position: absolute;
          left: 4px;
          top: 4px;
          border: 2px solid #000;
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        
        [data-theme="dark"] .loader::after {
          border: 2px solid #FFF;
        }
        
        @keyframes rotation {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .error-container {
          padding: 3rem;
          text-align: center;
          color: #dc2626;
          background: #fef2f2;
          min-height: 100vh;
          font-size: 1.25rem;
          font-weight: 500;
        }

        /* Modal styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: var(--overlay, rgba(0,0,0,0.45));
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: var(--bg-card, #fff);
          width: min(1400px, 95vw);
          max-height: 85vh;
          border-radius: 12px;
          box-shadow: 0 10px 30px var(--shadow-hover, rgba(0,0,0,0.2));
          display: flex;
          flex-direction: column;
        }
        
        @media (min-width: 1600px) {
          .modal-content {
            width: min(1600px, 95vw);
          }
        }
        
        @media (max-width: 768px) {
          .modal-content {
            width: 95vw;
            max-height: 90vh;
          }
        }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 18px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        .modal-close {
          border: 1px solid var(--border-color, #d1d5db);
          background: var(--bg-card, #fff);
          border-radius: 6px;
          width: 28px;
          height: 28px;
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
        }
        .modal-body {
          padding: 14px 18px;
          overflow: auto;
        }
        .items-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 12px;
        }
        .item-card {
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 8px;
          padding: 10px 12px;
          background: var(--bg-tertiary, #fafafa);
        }
        .item-title { font-weight: 600; color: var(--text-primary, #111827); margin-bottom: 6px; }
        .item-json {
          margin: 0;
          background: var(--bg-card, #fff);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 6px;
          padding: 8px;
          font-size: 12px;
          color: var(--text-primary, #111827);
          max-height: 260px;
          overflow: auto;
        }

        .table-wrap { 
          overflow-x: auto;
          overflow-y: auto;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Beautiful Scrollbar Styling */
        .table-wrap::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        
        .table-wrap::-webkit-scrollbar-track {
          background: var(--bg-tertiary, #f1f5f9);
          border-radius: 10px;
        }
        
        .table-wrap::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #cbd5e1, #94a3b8);
          border-radius: 10px;
          border: 2px solid var(--bg-tertiary, #f1f5f9);
        }
        
        .table-wrap::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #94a3b8, #64748b);
        }
        
        [data-theme="dark"] .table-wrap::-webkit-scrollbar-track {
          background: #1e293b;
        }
        
        [data-theme="dark"] .table-wrap::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #475569, #334155);
          border: 2px solid #1e293b;
        }
        
        [data-theme="dark"] .table-wrap::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #334155, #1e293b);
        }
        
        /* Firefox Scrollbar */
        .table-wrap {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 var(--bg-tertiary, #f1f5f9);
        }
        
        [data-theme="dark"] .table-wrap {
          scrollbar-color: #475569 #1e293b;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          min-width: max-content;
          table-layout: auto;
        }
        .items-table th, .items-table td {
          border: 1px solid var(--border-color, #e5e7eb);
          padding: 8px 10px;
          text-align: left;
          white-space: nowrap;
        }
        .items-table thead th {
          background: var(--bg-tertiary, #f3f4f6);
          color: var(--text-primary, #374151);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        [data-theme="dark"] .items-table thead th {
          background: #1f2937;
          color: #ffffff;
        }
        
        /* Flagged table row styling */
        .flagged-table-row {
          background-color: #fef3c7 !important;
        }
        .flagged-table-row td {
          background-color: #fef3c7 !important;
          color: #000000 !important;
        }
        [data-theme="dark"] .flagged-table-row {
          background-color: #78350f !important;
        }
        [data-theme="dark"] .flagged-table-row td {
          background-color: #78350f !important;
          color: #ffffff !important;
        }
        [data-theme="dark"] .flagged-table-row:hover {
          background-color: #92400e !important;
        }
        [data-theme="dark"] .flagged-table-row:hover td {
          background-color: #92400e !important;
          color: #ffffff !important;
        }
        
        /* Responsive table adjustments */
        @media (max-width: 768px) {
          .items-table {
            font-size: 12px;
          }
          .items-table th, .items-table td {
            padding: 6px 8px;
          }
          .chart-container .table-wrap {
            max-height: 400px;
          }
        }
        
        /* Export button styling (align with list pages) */
        .export-btn {
          padding: 8px 16px;
          border: 1px solid #3b82f6;
          border-radius: 4px;
          background: #3b82f6;
          font-size: 14px;
          cursor: pointer;
          color: #fff;
          font-weight: 500;
          transition: all 0.2s;
        }
        .export-btn:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;